import { FormControl } from '@/components/FormControl';
import { Input } from '@tigergraph/app-ui-lib/input';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Controller, useForm } from 'react-hook-form';
import { DBUser } from '@/pages/admin/user/type';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { FormControlOverrides } from 'baseui/form-control';
import { CopyIcon } from 'lucide-react';
import Generator from 'generate-password-ts';
import useCopyClipboard from 'react-use-clipboard';
import { useEffect } from 'react';
import { showToast } from '@/components/styledToasterContainer';
import { useMutationCreateDBUser, useMutationUpdateDBUser } from '@/pages/admin/user/hook';
import { WorkspaceT } from '@/pages/workgroup/type';
import { getErrorMessage } from '@/utils/utils';

export default function UserForm({
  dbUser,
  onCancel,
  workspace,
}: {
  dbUser?: DBUser;
  onCancel: () => void;
  workspace: WorkspaceT;
}) {
  const [css, theme] = useStyletron();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      username: dbUser?.username || '',
      password: dbUser?.password || '',
    },
  });
  const isUpdate = Boolean(dbUser);
  const password = watch('password');

  const [passwordCopied, setPasswordCopied] = useCopyClipboard(password, {
    successDuration: 1000,
  });

  useEffect(() => {
    if (passwordCopied) {
      showToast({
        kind: 'positive',
        message: 'password copied successfully.',
      });
    }
  }, [passwordCopied]);

  const formControlOverrides: FormControlOverrides = {
    Label: {
      style: () => {
        return {
          fontSize: '12px',
          lineHeight: '16px',
          marginBottom: '-4px',
        };
      },
    },
  };

  const createMutation = useMutationCreateDBUser();
  const updateMutation = useMutationUpdateDBUser();

  const onSubmit = async (formData: { username: string; password: string }) => {
    if (isUpdate) {
      updateMutation.mutate(
        {
          ...formData,
          groupID: workspace.workgroup_id,
          spaceID: workspace.workspace_id,
        },
        {
          onSuccess: () => {
            showToast({
              kind: 'positive',
              message: 'The user has been successfully updated.',
            });
            onCancel();
          },
          onError(error) {
            showToast({
              kind: 'negative',
              message: getErrorMessage(error),
            });
          },
        }
      );
    } else {
      createMutation.mutate(
        {
          ...formData,
          groupID: workspace.workgroup_id,
          spaceID: workspace.workspace_id,
        },
        {
          onSuccess: () => {
            showToast({
              kind: 'positive',
              message: 'The user has been successfully created.',
            });
            onCancel();
          },
          onError(error) {
            showToast({
              kind: 'negative',
              message: getErrorMessage(error),
            });
          },
        }
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={css({
        padding: '16px',
        borderBottom: isUpdate ? `1px solid ${theme.colors.divider}` : 'none',
      })}
      autoComplete="off"
    >
      <FormControl label="Username" error={errors.username?.message} overrides={formControlOverrides}>
        <Controller
          name="username"
          rules={{ required: 'required' }}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="compact"
              placeholder="Please enter username"
              autoComplete="off"
              error={Boolean(errors.username)}
              readOnly={isUpdate}
            />
          )}
        />
      </FormControl>
      <FormControl label="Password" error={errors.password?.message} overrides={formControlOverrides}>
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          })}
        >
          <div
            className={css({
              flex: 1,
            })}
          >
            <Controller
              name="password"
              rules={{ required: 'required' }}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  size="compact"
                  placeholder="Please enter password"
                  type="password"
                  autoComplete="new-password"
                  error={Boolean(errors.password)}
                  endEnhancer={
                    <Button
                      size="compact"
                      kind="text"
                      shape="square"
                      overrides={{
                        BaseButton: {
                          style: {
                            marginLeft: '8px',
                          },
                        },
                      }}
                      type="button"
                      onClick={setPasswordCopied}
                    >
                      <CopyIcon size={14} />
                    </Button>
                  }
                />
              )}
            />
          </div>
          <Button
            type="button"
            kind="secondary"
            onClick={() => setValue('password', Generator.generate({ length: 16, numbers: true }))}
          >
            Auto Generate
          </Button>
        </div>
      </FormControl>
      <div className="space-x-2 text-right">
        <Button kind="secondary" onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button
          type="submit"
          // isLoading={isLoading}
        >
          {isUpdate ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
